import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { getTokenInfo } from "../utils/auth";
import ChangePasswordModal from "./ChangePasswordModal";
interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const tokenInfo = getTokenInfo();
  const adminRole = tokenInfo?.role || "";

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/admin/login");
  };

  // Master admin's job is limited to overseeing registered accounts and
  // billing plans — nothing operational. All restaurant-running features
  // (orders, menu, reports, employees, locations, etc.) belong to the
  // location_admin who actually owns a restaurant.
  const masterAdminNavItems: Array<{
    path?: string;
    label: string;
    children?: Array<{ path: string; label: string }>;
  }> = [
    { path: "/admin/location-admins", label: "Location Admins" },
    { path: "/admin/plans", label: "Plans" },
    // Platform-wide defaults: currency, fallback logo, Apple Pass logo.
    { path: "/admin/platform-logo", label: "Platform Settings" },
    // Plan purchases paid by restaurant admins.
    { path: "/admin/plan-transactions", label: "Transactions" },
  ];

  const locationAdminNavItems: Array<{
    path?: string;
    label: string;
    children?: Array<{ path: string; label: string }>;
  }> = [
    { path: "/admin", label: "Dashboard" },
    {
      label: "Reports",
      children: [
        { path: "/admin/reports/sales", label: "Sales" },
        { path: "/admin/reports/payments", label: "Payments" },
        { path: "/admin/reports/items", label: "Items" },
        { path: "/admin/reports/taxes", label: "Sales Taxes" },
      ],
    },
    { path: "/admin/orders", label: "View Orders" },
    { path: "/admin/refunds", label: "Refunds" },
    { path: "/admin/category", label: "Menu Items" },
    { path: "/admin/addpreset", label: "Add Preset" },
    { path: "/admin/managetax", label: "Tax" },
    { path: "/admin/manage", label: "Employees" },
    { path: "/admin/ledger", label: "Ledger" },
    { path: "/admin/roles", label: "Roles" },
    { path: "/admin/loyalty", label: "User List" },
    { path: "/admin/discount", label: "Loyalty" },
    { path: "/admin/addhotelinformation", label: "Locations" },
    { path: "/admin/platform-logo", label: "Platform Logo" },
    { path: "/admin/theme", label: "Platform Theme" },
    { path: "/admin/choose-plan", label: "Billing" },
    { path: "/admin/developer-integrations", label: "Developer Integrations" },
    { path: "/admin/promotional-email", label: "Promotional Email" },
    { path: "/admin/reward-campaigns", label: "Reward Campaigns" },
  ];

  const navItems =
    adminRole === "master_admin" ? masterAdminNavItems : locationAdminNavItems;

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-white p-6 shadow-md flex flex-col 
          transition-transform duration-300 ease-in-out
          w-64 lg:w-[265px]  z-40
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        <h2 className="text-xl font-bold mb-8 mt-8 lg:mt-0">Admin Panel</h2>

        <nav className="flex-1 flex flex-col overflow-y-auto no-scrollbar pr-1">
          {navItems.map((item) => {
            if (item.children?.length) {
              const isParentActive = item.children.some((child) =>
                location.pathname.startsWith(child.path),
              );

              return (
                <div key={item.label} className="mb-2">
                  <button
                    type="button"
                    onClick={() => setReportsExpanded((prev) => !prev)}
                    className={`mb-1 flex w-full items-center justify-between rounded-md p-2 text-left font-semibold transition-all duration-200 ${
                      isParentActive
                        ? "bg-blue-50 text-blue-600"
                        : "text-black hover:bg-blue-50"
                    }`}
                  >
                    <span>{item.label}</span>
                    <span className="text-sm">
                      {reportsExpanded ? "▾" : "▸"}
                    </span>
                  </button>
                  {reportsExpanded && (
                    <div className="space-y-1 pl-3">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            style={{
                              backgroundColor: isChildActive
                                ? "#2563eb"
                                : "transparent",
                              color: isChildActive ? "white" : "black",
                            }}
                            className="block p-2 mb-1 font-semibold no-underline rounded-md transition-all
         duration-200 cursor-pointer hover:bg-blue-50 
        max-sm:w-full max-sm:my-1 max-sm:p-2.5"
                            onMouseEnter={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor =
                                  "#d5e2ff";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }
                            }}
                            onClick={() => setSidebarOpen(false)}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path || "/admin"}
                style={{
                  backgroundColor: isActive ? "#2563eb" : "transparent",
                  color: isActive ? "white" : "black",
                }}
                className="block p-2 mb-2 font-semibold no-underline rounded-md transition-all
         duration-200 cursor-pointer hover:bg-blue-50 
        max-sm:w-full max-sm:my-1 max-sm:p-2.5"
                onMouseEnter={(e) => {
                  if (!isActive)
                    e.currentTarget.style.backgroundColor = "#d5e2ff";
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    e.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={() => setSidebarOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <button
          className="
            p-2 mb-3 bg-white text-blue-600 border border-blue-600 rounded-md cursor-pointer
            transition-all duration-100 hover:bg-blue-50
            max-sm:w-[55%] max-sm:p-2.5
          "
          onClick={() => {
            setShowChangePassword(true);
            setSidebarOpen(false);
          }}
        >
          Change Password
        </button>

        <button
          className="
            p-2 bg-red-500 text-white border-none rounded-md cursor-pointer 
            transition-all duration-100 hover:bg-red-700 hover:scale-105 hover:shadow-lg 
            active:scale-100 active:shadow-none
            max-sm:w-[55%] max-sm:mt-3 max-sm:p-2.5
          "
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>

      {/* Hamburger button for tablets + mobile */}
      <button
        className={`
    fixed top-0 left-0 w-12/4 p-3 text-2xl bg-gray-100 shadow-sm cursor-pointer 
    z-50 lg:hidden
    ${sidebarOpen ? "hidden" : "block"}
  `}
        onClick={() => setSidebarOpen(true)}
      >
        ☰
      </button>
      {/* Overlay for mobile/tablet when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
      {/* Main Content   */}
      <div className="ml-0 lg:ml-[265px] flex-1 min-h-screen w-full ">
        <div className="mt-12 lg:mt-0">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;
